import * as THREE from "three";
import { HqMaterials } from "../hqMaterials";
import { GroundMesh } from "../groundMesh";
import { reducedVfx } from "../../ui/settings";

/**
 * Chimney smoke on the fortress (`base_t1..3` and the skins).
 *
 * The GLBs bake it as solid puffs: `smoke0..7` nodes, four to a chimney, whose `idle` clip grows each one from a
 * speck to full size as it rises 0.33 — and whose last three frames (0.1 s) interpolate straight back to the start
 * so the loop closes. Opaque, sharing the fortress's atlas material and merged into its rigid rig, they could not
 * fade; on the phone every puff was seen to rise and then get sucked back into the chimney (Kevin, 2026-09-23).
 *
 * So the baked puffs are hidden (their tracks dropped from every clip, their nodes scaled to nothing) and each
 * chimney mouth — the lowest puff's first `idle` key — emits soft round sprites instead: staggered puffs that rise,
 * swell, lean with the wind (GroundMesh.WindDir) and fade to nothing before they are recycled. One `Points` draw per
 * fortress, driven from `HqMaterials.time` in its own `onBeforeRender`, so the hub and the level both get it with no
 * update call. The lifecycle is `puffAt` (pure, unit-tested).
 */
export class ChimneySmoke {
  /** The baked puff nodes (`base_t1.smoke0` arrives as `base_t1smoke0`); not the damage anchors `dmg1_smoke*`. */
  static readonly Puff = /(?:^|[^_])smoke\d+$/;
  /** Seconds a puff lives. */
  static readonly Life = 3.2;
  /** Puffs in the air per chimney at once (evenly staggered over `Life`); fewer under Reduced effects. */
  static readonly Puffs = 8;
  static readonly PuffsReduced = 4;
  /** Anchors closer than this on the ground plane belong to one chimney, model units. */
  static readonly SameStack = 0.05;
  /** The baked puff mesh sits this far above its node: where the smoke leaves the chimney. */
  static readonly MouthLift = 0.03;
  /** Scale the baked puffs are held at: effectively nothing, but not a singular matrix. */
  static readonly Hidden = 1e-4;
  /** Random spread of a puff's start round the mouth, model units (± half). */
  static readonly Spread = 0.04;
  /** A puff's size varies by ± this share cycle to cycle. */
  static readonly SizeVary = 0.15;
  /** How fast a puff turns, rad/s either way — the lobes roll as it climbs. */
  static readonly Spin = 0.6;
  /** Puff colour: a warm light grey (hearth smoke, not the grey-black of the damage fires). */
  static readonly Colour = 0xd9d4ce;

  private static material: THREE.ShaderMaterial | null = null;
  private static readonly stripped = new WeakMap<THREE.AnimationClip, THREE.AnimationClip>();

  readonly points: THREE.Points;
  private readonly mouths: THREE.Vector3[];
  private readonly perStack: number;
  private readonly pos: Float32Array;
  private readonly size: Float32Array;
  private readonly alpha: Float32Array;
  private readonly spin: Float32Array;
  private readonly state: PuffState = { rise: 0, drift: 0, size: 0, alpha: 0 };
  private readonly wind = new THREE.Vector3();
  private readonly rot = new THREE.Matrix4();
  private static readonly viewport = new THREE.Vector4();

  /**
   * Fits a loaded fortress model with chimney smoke: hides the baked puffs, adds the emitter under `model`, and
   * returns `clips` without the puff tracks (the clips to rig the model with). A model with no puffs is left alone.
   */
  static install(model: THREE.Object3D, clips: readonly THREE.AnimationClip[]): readonly THREE.AnimationClip[] {
    const anchors: THREE.Object3D[] = [];
    model.traverse((o) => { if (ChimneySmoke.Puff.test(o.name)) anchors.push(o); });
    if (anchors.length === 0) return clips;
    const idle = THREE.AnimationClip.findByName(clips as THREE.AnimationClip[], "idle");
    model.updateMatrixWorld(true);
    const toModel = model.matrixWorld.clone().invert();
    const mouths: THREE.Vector3[] = [];
    for (const a of anchors) {
      const p = a.parent!.localToWorld(ChimneySmoke.startOf(a, idle)).applyMatrix4(toModel);
      const same = mouths.find((m) => Math.hypot(m.x - p.x, m.z - p.z) < ChimneySmoke.SameStack);
      if (!same) mouths.push(p); else if (p.y < same.y) same.copy(p);
      a.scale.setScalar(ChimneySmoke.Hidden);
    }
    for (const m of mouths) m.y += ChimneySmoke.MouthLift;
    model.add(new ChimneySmoke(mouths, ChimneySmoke.reduced() ? ChimneySmoke.PuffsReduced : ChimneySmoke.Puffs).points);
    const names = new Set(anchors.map((a) => a.name));
    return clips.map((c) => ChimneySmoke.strip(c, names));
  }

  /** Reduced effects (settings may be unreadable — no storage — in which case the full set). */
  private static reduced(): boolean { try { return reducedVfx(); } catch { return false; } }

  /** Where the idle clip starts a puff node (its first position key), in its parent's space; else where it stands. */
  private static startOf(node: THREE.Object3D, idle: THREE.AnimationClip | null): THREE.Vector3 {
    const track = idle?.tracks.find((t) => t.name === `${node.name}.position`);
    return track ? new THREE.Vector3(track.values[0], track.values[1], track.values[2]) : node.position.clone();
  }

  /** `clip` without the tracks of the named nodes (cached: every clone of a model shares its clips). */
  private static strip(clip: THREE.AnimationClip, names: ReadonlySet<string>): THREE.AnimationClip {
    let out = ChimneySmoke.stripped.get(clip);
    if (!out) {
      const tracks = clip.tracks.filter((t) => !names.has(t.name.slice(0, t.name.lastIndexOf("."))));
      out = tracks.length === clip.tracks.length ? clip : new THREE.AnimationClip(clip.name, clip.duration, tracks, clip.blendMode);
      ChimneySmoke.stripped.set(clip, out);
    }
    return out;
  }

  constructor(mouths: THREE.Vector3[], perStack: number) {
    this.mouths = mouths;
    this.perStack = perStack;
    const n = mouths.length * perStack;
    this.pos = new Float32Array(n * 3);
    this.size = new Float32Array(n);
    this.alpha = new Float32Array(n);
    this.spin = new Float32Array(n);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute("aSize", new THREE.BufferAttribute(this.size, 1).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute("aAlpha", new THREE.BufferAttribute(this.alpha, 1).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute("aSpin", new THREE.BufferAttribute(this.spin, 1).setUsage(THREE.DynamicDrawUsage));
    this.points = new THREE.Points(geo, ChimneySmoke.sharedMaterial());
    this.points.name = "chimney_smoke";
    this.points.userData.ownsGeometry = true;             // per model: RigidRig.dispose frees it with the model
    this.points.frustumCulled = false;                    // the puffs roam above the model's bounds
    this.points.castShadow = false;
    this.points.receiveShadow = false;
    this.points.onBeforeRender = (renderer) => {
      renderer.getCurrentViewport(ChimneySmoke.viewport);
      (this.points.material as THREE.ShaderMaterial).uniforms.uHalfH.value = ChimneySmoke.viewport.w / 2;
      this.update(HqMaterials.time.value);
    };
    this.update(0);
  }

  /** Writes every puff for time `t` (seconds). The attributes reach the GPU on the next frame's upload. */
  update(t: number): void {
    // the wind blows in world space; the model turns (its heading, the hub's sway), so bring it into model space
    const [wx, wz] = GroundMesh.WindDir;
    this.rot.extractRotation(this.points.matrixWorld).transpose();
    this.wind.set(wx, 0, wz).applyMatrix4(this.rot);
    this.wind.y = 0;
    this.wind.normalize();
    let i = 0;
    for (let s = 0; s < this.mouths.length; s++) {
      const m = this.mouths[s];
      for (let k = 0; k < this.perStack; k++, i++) {
        const { age, cycle } = puffAge(t, k, this.perStack, s);
        const st = puffAt(age, this.state);
        const jx = (hash(cycle, k, s, 1) - 0.5) * ChimneySmoke.Spread, jz = (hash(cycle, k, s, 2) - 0.5) * ChimneySmoke.Spread;
        this.pos[i * 3] = m.x + jx + this.wind.x * st.drift;
        this.pos[i * 3 + 1] = m.y + st.rise;
        this.pos[i * 3 + 2] = m.z + jz + this.wind.z * st.drift;
        this.size[i] = st.size * (1 + (hash(cycle, k, s, 3) - 0.5) * 2 * ChimneySmoke.SizeVary);
        this.alpha[i] = st.alpha;
        this.spin[i] = hash(cycle, k, s, 4) * Math.PI * 2 + (hash(cycle, k, s, 5) - 0.5) * 2 * ChimneySmoke.Spin * age * ChimneySmoke.Life;
      }
    }
    const g = this.points.geometry;
    for (const name of ["position", "aSize", "aAlpha", "aSpin"]) g.getAttribute(name).needsUpdate = true;
  }

  private static sharedMaterial(): THREE.ShaderMaterial {
    if (ChimneySmoke.material) return ChimneySmoke.material;
    ChimneySmoke.material = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false,
      uniforms: { uHalfH: { value: 400 }, uColor: { value: new THREE.Color(ChimneySmoke.Colour) } },
      vertexShader: /* glsl */ `
        attribute float aSize;
        attribute float aAlpha;
        attribute float aSpin;
        uniform float uHalfH;
        varying float vAlpha;
        varying float vSpin;
        void main() {
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          // aSize is a diameter in model units; the model's scale and the projection turn it into pixels
          float px = aSize * length(modelMatrix[0].xyz) * projectionMatrix[1][1] * uHalfH;
          gl_PointSize = isOrthographic ? px : px / -mv.z;
          gl_Position = projectionMatrix * mv;
          vAlpha = aAlpha;
          vSpin = aSpin;
        }`,
      fragmentShader: /* glsl */ `
        uniform vec3 uColor;
        varying float vAlpha;
        varying float vSpin;
        void main() {
          vec2 p = gl_PointCoord * 2.0 - 1.0;
          float shade = mix(0.78, 1.0, 0.5 - 0.5 * p.y);     // lit from above: gl_PointCoord.y runs downward
          float c = cos(vSpin), s = sin(vSpin);
          p = mat2(c, -s, s, c) * p;
          // three soft lobes: a billow, not a disc
          vec2 a = p - vec2(0.22, 0.12), b = p + vec2(0.24, -0.04), d = p - vec2(-0.02, -0.24);
          float lobes = exp(-5.0 * dot(a, a)) + exp(-5.5 * dot(b, b)) + exp(-4.5 * dot(d, d));
          float alpha = clamp(lobes, 0.0, 1.0) * (1.0 - smoothstep(0.75, 1.0, length(p))) * vAlpha;
          if (alpha < 0.004) discard;
          gl_FragColor = vec4(uColor * shade, alpha);
          #include <tonemapping_fragment>
          #include <colorspace_fragment>
        }`,
    });
    return ChimneySmoke.material;
  }
}

/**
 * A puff's shape over its life, `age` 0 → 1 (model units): it climbs fast off the chimney and slows (never coming
 * back down), leans further downwind as it goes, swells, and fades in quickly then out to exactly 0 at `age` 1 — so
 * the recycle to the mouth is never seen.
 */
export const PUFF = {
  /** Height gained over a life. */
  rise: 0.7,
  /** Distance carried downwind over a life. */
  drift: 0.32,
  /** Diameter at the mouth and at the end. */
  size0: 0.08, size1: 0.5,
  /** Peak opacity, reached at `fadeIn` of the life. */
  alpha: 0.6, fadeIn: 0.07,
  /** Share of the rise that is steady climb; the rest is the chimney's push, spent early. */
  climb: 0.6,
  /** Shape of the fade-out (higher thins it sooner). */
  fadePow: 1.4,
} as const;

export interface PuffState { rise: number; drift: number; size: number; alpha: number }

export function puffAt(age: number, out: PuffState = { rise: 0, drift: 0, size: 0, alpha: 0 }): PuffState {
  const u = Math.min(1, Math.max(0, age));
  const push = 1 - (1 - u) * (1 - u);                    // ease-out: the draught's push is spent early
  out.rise = PUFF.rise * (PUFF.climb * u + (1 - PUFF.climb) * push);
  out.drift = PUFF.drift * Math.pow(u, 1.3);             // the wind gets hold of it as it clears the roof
  out.size = PUFF.size0 + (PUFF.size1 - PUFF.size0) * (1 - Math.pow(1 - u, 1.6));
  out.alpha = u < PUFF.fadeIn
    ? PUFF.alpha * u / PUFF.fadeIn
    : PUFF.alpha * Math.pow(1 - (u - PUFF.fadeIn) / (1 - PUFF.fadeIn), PUFF.fadePow);
  return out;
}

/**
 * Puff `index` of `count` on chimney `stack` at time `t`: how far through its life it is, and which life (`cycle`,
 * which picks its jitter). The puffs are staggered evenly and each chimney is offset, so the column never pulses.
 */
export function puffAge(t: number, index: number, count: number, stack: number): { age: number; cycle: number } {
  const x = t / ChimneySmoke.Life + index / count + stack * STACK_OFFSET;
  const cycle = Math.floor(x);
  return { age: x - cycle, cycle };
}
/** Phase offset between chimneys, in lives: irrational-ish so two stacks never puff in step. */
const STACK_OFFSET = 0.377;

function hash(a: number, b: number, c: number, d: number): number {
  const s = Math.sin(a * 12.9898 + b * 78.233 + c * 37.719 + d * 4.581) * 43758.5453;
  return s - Math.floor(s);
}
